import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Plus, Pencil, KeyRound, ShieldOff, ShieldCheck, Users as UsersIcon,
  Search, RotateCcw, Eye, History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { classifyErr, copyToClipboard, debounce } from "@/lib/admin";
import { useAuth } from "@/lib/auth";
import UserForm from "@/pages/admin/UserForm";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import ChangeHistoryPanel from "@/components/bulk/ChangeHistoryPanel";
import WorkflowPermissionsPanel from "@/components/WorkflowPermissionsPanel";

const PAGE_SIZES = [10, 20, 50];

export default function UsersList() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkEditId = searchParams.get("edit");
  const deepLinkHandled = useRef(false);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [roles, setRoles] = useState([]);
  const [zones, setZones] = useState([]);

  const [roleFilter, setRoleFilter] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(() => searchParams.get("q") || "");

  const [busy, setBusy] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [workflowFor, setWorkflowFor] = useState(null);

  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [tempPw, setTempPw] = useState(null); // { user, temp_password }
  const [historyFor, setHistoryFor] = useState(null); // { id, name } | { all: true }

  const canManage = hasPermission("users.manage");
  const canExport = hasPermission("bulk.export") || hasPermission("users.view");

  const setDebouncedQNow = useMemo(() => debounce((v) => setDebouncedQ(v), 350), []);

  useEffect(() => {
    setDebouncedQNow(q);
  }, [q, setDebouncedQNow]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: pageSize };
      if (roleFilter) params.role_code = roleFilter;
      if (zoneFilter) params.zone_id = zoneFilter;
      if (statusFilter) params.status = statusFilter;
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get("/users", { params });
      setUsers(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(classifyErr(err));
    } finally {
      setBusy(false);
    }
  }, [page, pageSize, roleFilter, zoneFilter, statusFilter, debouncedQ]);

  useEffect(() => {
    (async () => {
      try {
        const [r, z] = await Promise.all([api.get("/roles"), api.get("/zones")]);
        setRoles(r.data.items);
        setZones(z.data.items);
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Deep-link support (e.g. from global search): ?q=<email/phone>&edit=<id>
  // opens that user's edit form directly once it shows up in the loaded page.
  useEffect(() => {
    if (deepLinkHandled.current || !deepLinkEditId || users.length === 0) return;
    const u = users.find((r) => r.id === deepLinkEditId);
    if (u) {
      deepLinkHandled.current = true;
      openEdit(u);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("edit");
        return next;
      }, { replace: true });
    }
  }, [users, deepLinkEditId]);

  const zoneById = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);

  const resetFilters = () => {
    setRoleFilter("");
    setZoneFilter("");
    setStatusFilter("");
    setQ("");
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setFormOpen(true);
  };

  const onCreated = ({ user, initial_password }) => {
    setFormOpen(false);
    if (initial_password) setTempPw({ user, temp_password: initial_password, action: "create" });
    load();
  };
  const onUpdated = () => {
    setFormOpen(false);
    load();
  };

  const doReset = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTempPw({ user: u, temp_password: data.temp_password, action: "reset" });
    } catch (err) {
      toast.error(classifyErr(err));
    }
  };

  const doDeactivate = async () => {
    const u = confirmDeactivate;
    if (!u) return;
    try {
      if (u.status === "active") {
        await api.post(`/users/${u.id}/deactivate`);
        toast.success(`${u.name} deactivated`);
      } else {
        await api.post(`/users/${u.id}/reactivate`);
        toast.success(`${u.name} reactivated`);
      }
      setConfirmDeactivate(null);
      load();
    } catch (err) {
      toast.error(classifyErr(err));
      setConfirmDeactivate(null);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-users-page" className="space-y-5">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Users" }]}
        title="Users"
        subtitle="Manage office & field users, roles, zones, and reporting."
        favorite={{ key: "admin.users", type: "screen", label: "Users", route: "/admin/users" }}
        testid="users-page-header"
        actions={
          canManage ? (
            <>
              <BulkActionsBar
                masterKey="users"
                label="Users"
                caption="Users"
                hint="Add or update the users themselves (name, role, phone, zone…) in bulk"
                canImport={canManage}
                canExport={canExport}
                buildExportFilters={() => {
                  const f = {};
                  if (roleFilter) f.role_code = roleFilter;
                  if (zoneFilter) f.zone_id = zoneFilter;
                  if (statusFilter) f.status = statusFilter;
                  if (debouncedQ) f.q = debouncedQ;
                  return f;
                }}
                onImportDone={() => load()}
              />
              {/* Bulk user↔distributor mapping — one row per (user_code,
                  distributor_code, action=ADD|REMOVE). Backend spec already
                  existed (bulk_io/specs/user_distributor_map.py) but had no
                  UI entry point until now. */}
              <BulkActionsBar
                masterKey="user_distributor_map"
                label="Distributor Mappings"
                caption="Distributor mapping"
                hint="Decide which distributors each SE/ASM works with: add or remove user-to-distributor links in bulk"
                canImport={canManage}
                canExport={canExport}
                onImportDone={() => load()}
              />
              <Button
                variant="outline"
                onClick={() => setHistoryFor({ all: true })}
                data-testid="admin-users-history-all"
                className="h-10"
                title="View change history for all users"
              >
                <History className="h-4 w-4 mr-2" /> History
              </Button>
              <Button
                data-testid="admin-users-new"
                onClick={openCreate}
                className="tulsi-gradient text-white h-10 font-semibold rounded-xl shadow-md shadow-[#E5197F]/20"
              >
                <Plus className="h-4 w-4 mr-2" /> New user
              </Button>
            </>
          ) : null
        }
      />

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="col-span-1 sm:col-span-2">
              <Label className="text-xs text-neutral-500">Search</Label>
              <div className="relative mt-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  data-testid="admin-users-search"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Name, email or phone…"
                  className="pl-9 h-10"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Role</Label>
              <Select value={roleFilter || "all"} onValueChange={(v) => { setRoleFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="admin-users-role-filter" className="h-10 mt-1">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Zone</Label>
              <Select value={zoneFilter || "all"} onValueChange={(v) => { setZoneFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="admin-users-zone-filter" className="h-10 mt-1">
                  <SelectValue placeholder="All zones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All zones</SelectItem>
                  {zones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Status</Label>
              <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="admin-users-status-filter" className="h-10 mt-1">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={resetFilters}
                data-testid="admin-users-reset-filters"
                className="h-10"
              >
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="w-[220px]">User</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Zones</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {busy && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-neutral-500 py-10">
                    Loading users…
                  </TableCell>
                </TableRow>
              )}
              {!busy && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-neutral-500 py-10">
                    No users match your filters.
                  </TableCell>
                </TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`admin-users-row-${u.id}`}>
                  <TableCell>
                    <div className="font-semibold text-neutral-800">{u.name}</div>
                    <div className="text-[11px] text-neutral-400">
                      Manager: {u.reporting_manager_id ? (u.reporting_manager_name || "—") : "—"}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-neutral-700">
                    <div>{u.email || "—"}</div>
                    <div className="text-neutral-500">{u.phone || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="border-[#6D2C91]/30 text-[#6D2C91] bg-[#6D2C91]/5"
                    >
                      {u.role_code}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(u.assigned_zone_ids || []).map((zid) => (
                        <span
                          key={zid}
                          className="text-[11px] px-1.5 py-0.5 rounded bg-[#E5197F]/10 text-[#E5197F]"
                        >
                          {zoneById[zid]?.code || zid.slice(0, 4)}
                        </span>
                      ))}
                      {!u.assigned_zone_ids?.length && <span className="text-xs text-neutral-400">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.status === "active" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                    ) : (
                      <Badge className="bg-neutral-200 text-neutral-700 hover:bg-neutral-200">
                        {u.status}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/admin/users/${u.id}/visibility`)}
                        data-testid={`admin-users-visibility-${u.id}`}
                        title="Manage visibility"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setWorkflowFor(u)}
                          data-testid={`admin-users-workflow-${u.id}`}
                          title="Workflow permissions & distributors"
                          className="text-[#6D2C91] hover:text-[#5a2378]"
                        >
                          <ShieldCheck className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setHistoryFor({ id: u.id, name: u.name })}
                        data-testid={`admin-users-history-${u.id}`}
                        title="Change history"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(u)}
                            data-testid={`admin-users-edit-${u.id}`}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => doReset(u)}
                            data-testid={`admin-users-reset-${u.id}`}
                            title="Reset password"
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmDeactivate(u)}
                            data-testid={`admin-users-toggle-${u.id}`}
                            title={u.status === "active" ? "Deactivate" : "Reactivate"}
                            className={u.status === "active" ? "text-red-500 hover:text-red-600" : "text-emerald-600 hover:text-emerald-700"}
                          >
                            {u.status === "active" ? <ShieldOff className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100 text-sm">
          <div className="text-neutral-500" data-testid="admin-users-total">
            {total} user{total === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-neutral-500">Rows:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-[76px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-users-prev">Prev</Button>
              <span className="px-2 text-neutral-600">
                {page} / {pageCount}
              </span>
              <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)} data-testid="admin-users-next">Next</Button>
            </div>
          </div>
        </div>
      </Card>

      <UserForm
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        roles={roles}
        zones={zones}
        onCreated={onCreated}
        onUpdated={onUpdated}
      />

      <Dialog open={!!confirmDeactivate} onOpenChange={(v) => !v && setConfirmDeactivate(null)}>
        <DialogContent data-testid="admin-users-confirm-toggle">
          <DialogHeader>
            <DialogTitle>
              {confirmDeactivate?.status === "active" ? "Deactivate user?" : "Reactivate user?"}
            </DialogTitle>
            <DialogDescription>
              {confirmDeactivate?.status === "active"
                ? `${confirmDeactivate?.name} will no longer be able to log in.`
                : `${confirmDeactivate?.name} will be able to log in again.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeactivate(null)}>Cancel</Button>
            <Button
              onClick={doDeactivate}
              className={confirmDeactivate?.status === "active" ? "bg-red-600 hover:bg-red-700 text-white" : "tulsi-gradient text-white"}
              data-testid="admin-users-confirm-toggle-yes"
            >
              {confirmDeactivate?.status === "active" ? "Deactivate" : "Reactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPw} onOpenChange={(v) => !v && setTempPw(null)}>
        <DialogContent data-testid="admin-users-temp-pw-modal">          <DialogHeader>
            <DialogTitle>Temporary password</DialogTitle>
            <DialogDescription>
              Share this with <span className="font-medium">{tempPw?.user?.name}</span> — it will not be shown again.
              They will be forced to change it on next login.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-neutral-900 text-white font-mono text-lg tracking-wider py-3 px-4 flex items-center justify-between">
            <span data-testid="admin-users-temp-pw-value">{tempPw?.temp_password}</span>
            <Button
              size="sm"
              className="bg-[#FFD93D] text-neutral-900 hover:bg-[#f5c916]"
              data-testid="admin-users-temp-pw-copy"
              onClick={() => {
                copyToClipboard(tempPw.temp_password);
                toast.success("Password copied to clipboard");
              }}
            >
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setTempPw(null)}
              className="tulsi-gradient text-white"
              data-testid="admin-users-temp-pw-close"
            >
              I've saved it — close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChangeHistoryPanel
        open={!!historyFor}
        onOpenChange={(v) => !v && setHistoryFor(null)}
        entityType="users"
        entityId={historyFor?.id || null}
        title={historyFor?.all ? "Users — all changes" : `History: ${historyFor?.name || ""}`}
      />
      <WorkflowPermissionsPanel
        open={!!workflowFor}
        onOpenChange={(v) => !v && setWorkflowFor(null)}
        targetUser={workflowFor}
      />
    </div>
  );
}
